import * as Portal from '@radix-ui/react-portal'
import React, { useEffect, useRef, useState } from 'react'
import { usePathPrefix } from '../../hooks/usePathPrefix'
// import ContentBlocks from './ContentBlocks'
import { createHistory } from '@reach/router'
import * as bodyScrollLock from 'body-scroll-lock'
import { AnimatePresence, motion } from 'framer-motion'
import { useDataLayer } from '../../hooks/useDataLayer'
import ContentBlock from '../molecules/ContentBlock'

const Card = ({ card, variant }) => {
  const dataLayer = useDataLayer()
  const ref = useRef()
  const prefix = usePathPrefix()
  const cardContentBlocks = card?.contentBlocks
  const [open, setOpen] = useState(false)
  const cardDetails = card?.cardDetails?.find(
    ({ __typename }) =>
      __typename === 'CraftContent_cardDetails_cardSummary_BlockType',
  )
  const isAnimation = {
    isAnimation: true,
  }

  const handleClick = () => {
    if (cardDetails?.variant === 'noLink') {
      return
    }

    dataLayer.push({
      event: 'userinteraction',
      eventCategory: 'Teaser',
      eventAction: 'click',
      eventLabel: cardDetails?.heading,
      event_location: null,
    })

    window?.history?.pushState({}, '', `${prefix}/card/${card.slug}`)
    setOpen(true)

    dataLayer.push({
      event: 'pageviewevent',
      page_path: `${prefix}/card/${card.slug}`,
      event_location: null,
    })
  }

  const handleClose = () => {
    setOpen(false)
    window?.history.back()
  }

  useEffect(() => {
    let unsubscribe

    if (open) {
      bodyScrollLock.disableBodyScroll(ref.current)

      const history = createHistory(window)
      unsubscribe = history.listen((loc, action) => {
        if (loc.action === 'POP') {
          setOpen(false)
        }
      })

      return
    }

    if (ref.current) {
      bodyScrollLock.enableBodyScroll(ref.current)
    }

    if (unsubscribe) {
      unsubscribe()
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`block text-left ${
          variant === 'circleImage' && cardDetails?.image[0]?.url
            ? 'mt-[75px] pt-[99px]'
            : ''
        } ${!cardDetails?.image[0]?.url && 'pt-7'} ${
          cardDetails?.variant === 'noLink' && 'pointer-events-none'
        } px-7 pb-9 relative w-64 group transition-all`}
      >
        <div
          className={`bg-white shadow-box rounded-[5px] absolute inset-0 -z-1 group-hover:-top-2.5 ${
            variant !== 'circleImage' && cardDetails?.image[0]?.url
              ? 'group-hover:bottom-5'
              : 'group-hover:bottom-2.5'
          } transition-all`}
        />
        {variant === 'circleImage' ? (
          <div
            className="absolute left-7 w-[150px] h-[150px] -top-[75px] rounded-full bg-center bg-cover"
            style={{ backgroundImage: `url(${cardDetails?.image[0]?.url})` }}
          />
        ) : (
          <>
            {cardDetails?.image[0]?.url && (
              <div className="mb-6 -mx-7">
                <img
                  src={cardDetails?.image[0]?.url}
                  alt=""
                  className="w-full h-auto relative z-3 transition-all group-hover:-mt-2.5 rounded-t-[5px]"
                />
              </div>
            )}
          </>
        )}
        {cardDetails?.heading && (
          <h3 className="mb-3 font-bold u-p group-hover:text-red-as">
            {cardDetails?.heading}
          </h3>
        )}
        {cardDetails?.cardValue && (
          <h3 className="mb-3 u-h1">{cardDetails?.cardValue}</h3>
        )}
        <div
          className="u-p link-wrapper"
          dangerouslySetInnerHTML={{ __html: cardDetails?.description }}
        />

        {cardDetails?.variant !== 'noLink' && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="16"
            fill="none"
            viewBox="0 0 20 16"
            className="absolute text-black right-4 bottom-4 group-hover:text-red-as group-hover:bottom-6.5 transition-all"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 1l7 7m0 0l-7 7m7-7H1"
            ></path>
          </svg>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <Portal.Root>
            <motion.div
              ref={ref}
              className="fixed inset-0 z-[999] bg-gray-1 pt-8 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { ease: 'circOut' } }}
              exit={{
                opacity: 0,
                transition: { ease: 'circIn', duration: 0.1, delay: 0.4 },
              }}
              transition={{ duration: 0.7 }}
            >
              <div className="fixed top-0 left-0 right-0">
                <motion.div
                  className="container relative"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.5 } }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <button
                    type="button"
                    className="right-4 md:right-2 2xl:right-10 absolute flex items-center justify-center bg-black rounded-full top-8 w-[54px] h-[54px] z-50 transition-all hover:bg-white hover:text-black text-white hover:shadow-tpmp"
                    onClick={handleClose}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      fill="none"
                      viewBox="0 0 22 22"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 5l6 6-6 6M17 5l-6 6 6 6"
                      ></path>
                    </svg>
                  </button>
                </motion.div>
              </div>

              {cardContentBlocks.map((contentBlock, index) => {
                const cb = { ...contentBlock, ...isAnimation }

                return (
                  <motion.div
                    key={cb.id}
                    initial={{ opacity: 0, y: 100 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        delay: index ? 0.3 + index * 0.1 : 0,
                        ease: 'easeInOut',
                        duration: 0.5,
                      },
                    }}
                    exit={{
                      opacity: 0,
                      y: 0,
                      transition: {
                        delay: 0,
                        ease: 'easeInOut',
                        duration: 0.5,
                      },
                    }}
                  >
                    <ContentBlock {...cb} />
                  </motion.div>
                )
              })}
            </motion.div>
            {/* </div> */}
          </Portal.Root>
        )}
      </AnimatePresence>
    </>
  )
}

export default Card
